import React, { useState, useRef, useEffect } from "react";
import CustomJoditEditor from "../../../../components/Editor/JoditEditor";
import FormDialog from "../../../../components/Dialog/FormDialog";
import { IconButton, TextField, Tooltip } from "@mui/material";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import { CustomButton } from "../../../../components/Button/Button";
import { stickyNoteColors } from "../../../../utils/Enums";
import "../MyPlannerGlobalNote.scss";
import { RFHTextField } from "../../../../components/RFH";
import { FormProvider, useForm } from "react-hook-form";

/**
 * Reusable Note Editor component with color picker and save/delete functionality
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog is closed
 * @param {function} props.onSave - Function to call when note is saved
 * @param {function} props.onDelete - Function to call when note is deleted
 * @param {Object} props.noteData - Note data object {id, content, color_code, created_at}
 * @param {function} props.onNoteChange - Function to call when note data changes
 * @param {boolean} props.isLoading - Whether a save/delete operation is in progress
 * @param {string} props.width - Width of the dialog (default: "591px")
 */
const NoteEditor = ({
  open,
  onClose,
  onSave,
  onDelete,
  noteData,
  onNoteChange,
  isLoading = false,
  width = "591px"
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const colorPickerRef = useRef(null);
  const colorButtonRef = useRef(null);

  // Effect to handle clicks outside the color picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only run this effect if the color picker is open
      if (!showColorPicker) return;
      
      // Check if the click was outside both the color picker and the color button
      if (
        colorPickerRef.current && 
        !colorPickerRef.current.contains(event.target) &&
        colorButtonRef.current && 
        !colorButtonRef.current.contains(event.target)
      ) {
        setShowColorPicker(false);
      }
    };

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColorPicker]);

  const toggleColorPicker = (e) => {
    // Stop propagation to prevent immediate closing
    e.stopPropagation();
    setShowColorPicker(!showColorPicker);
  };

  const handleColorSelect = (color) => {
    onNoteChange({
      ...noteData,
      color_code: color
    });
    // Close the color picker after selection
    setShowColorPicker(false);
  };

  const handleContentChange = (newContent) => {
    onNoteChange({
      ...noteData,
      content: newContent
    });
  };

  // Format the created date for display if available
  const formattedDate = noteData.created_at 
    ? new Date(noteData.created_at).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      })
    : "";

  const methods = useForm();
  useEffect(() => {
    methods.setValue("title", noteData?.title || "");
  }, [noteData?.title]);
  
  const onSubmit = (data) => {
    console.log("data");
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>

  
    <FormDialog
      open={open}
      handleClose={onClose}
      handleSubmit={onSave}
      width={width}
      className={"global-note-dialog"}
      isLoading={isLoading}
      disabled={isLoading}
      crossIcon={false}
    >

      <RFHTextField
        placeholder="Title"
        // value={noteData?.title || ''}
        className="note-title-input"
        inputProps={{
          maxLength: 150,
        }}
        sx={{
          backgroundColor: `${noteData?.color_code} !important`,
        }}
        name="title"
        onChange={(e) => {
          methods.setValue("title", e.target.value);
          onNoteChange({
            ...noteData,
            title: e.target.value
          });
        }}
        autoFocus={!noteData?.title && !noteData?.content}
      />
      <div className="global-note-editor-container">
        <CustomJoditEditor
          value={noteData.content}
          onBlur={handleContentChange}
          style={{
            placeholder: "Write a note...",
          }}
          backgroundColor={noteData.color_code || "#FFFFA5"}
          autoFocus={false}
        />
        <div
          className="editor-toolbar"
          style={{ backgroundColor: noteData.color_code || "#FFFFA5" }}
        >
          <div className="d-flex align-items-center">
            <Tooltip title="Change background color">
              <IconButton
                className="color-picker-button"
                onClick={toggleColorPicker}
                aria-label="Change background color"
                ref={colorButtonRef}
              >
                <ColorLensIcon />
              </IconButton>
            </Tooltip>
            {formattedDate && (
              <span className="note-date">{formattedDate}</span>
            )}
          </div>
          <div className="d-flex note-button-container">
            <CustomButton
              variant="text"
              color="secondary"
              size="small"
              onClick={onDelete}
              className="delete-button"
              disabled={isLoading}
            >
              Delete
            </CustomButton>

            <CustomButton
              variant="contained"
              color="primary"
              size="small"
              onClick={onSave}
              className="save-button"
              disabled={isLoading}
            >
              Save
            </CustomButton>
          </div>
        </div>

        <div
          className={`color-picker-panel ${showColorPicker ? "open" : ""}`}
          ref={colorPickerRef}
        >
          <div className="color-picker-content">
            <div className="color-grid">
              {stickyNoteColors.map((color) => (
                <div
                  key={color.color_code || color.hex}
                  className={`color-swatch ${
                    noteData.color_code === (color.color_code || color.hex)
                      ? "selected"
                      : ""
                  }`}
                  style={{ backgroundColor: color.color_code || color.hex }}
                  onClick={() =>
                    handleColorSelect(color.color_code || color.hex)
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </FormDialog>
    </form>
    </FormProvider>
  );
};

export default NoteEditor; 